import { Card, CardContent, Typography, Box, useTheme, useMediaQuery } from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { FacturaInforme } from '../Informes';
import { formatCurrency } from '../../../../utils/Factura';

interface GraficoEstadoCobrosProps {
  facturas: FacturaInforme[];
}

const CustomPieTooltip = ({ active, payload, moneda }: any) => {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0].payload;
  return (
    <Box sx={{
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'grey.300',
      p: 1,
      borderRadius: 1,
      boxShadow: 1,
      minWidth: 120,
      textAlign: 'center'
    }}>
      <Typography variant="subtitle2">{data.name}</Typography>
      <Typography variant="h6" fontWeight={600}>
        {formatCurrency(data.total, moneda)}
      </Typography>
    </Box>
  );
};

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, payload }: any) => {
  if (payload.total === 0) return null;
  const radius = innerRadius + (outerRadius - innerRadius) / 2;
  const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
  const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);

  const text = percent === 1 ? "100%" : `${(percent * 100).toFixed(2).replace(".", ",")}%`;

  return (
    <text
      x={x}
      y={y}
      fill="white"
      fontWeight={700}
      fontSize={18}
      textAnchor="middle"
      dominantBaseline="central"
    >
      {text}
    </text>
  );
};

const COLORS = ['rgb(46, 125, 50)', 'rgb(211, 47, 47)'];

const EstadoCobrosPie = ({ facturas, moneda, titulo }: { facturas: FacturaInforme[]; moneda: string; titulo: string }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const pagadas = facturas.filter(f => f.estadoPago === 'Pagada');
  const pendientes = facturas.filter(f => f.estadoPago === 'Pendiente');

  const data = [
    { name: 'Pagadas', value: pagadas.reduce((s, f) => s + f.total, 0), total: pagadas.reduce((s, f) => s + f.total, 0) },
    { name: 'Pendientes', value: pendientes.reduce((s, f) => s + f.total, 0), total: pendientes.reduce((s, f) => s + f.total, 0) },
  ].filter(d => d.value > 0);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {titulo}
        </Typography>
        <Box height={300}>
          <ResponsiveContainer initialDimension={{ width: 300, height: 300 }} width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                outerRadius={100}
                label={renderCustomizedLabel}
                labelLine={false}
              >
                {data.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index]} />
                ))}
              </Pie>
              <Tooltip content={<CustomPieTooltip moneda={moneda} />} />
              {!isMobile &&
              <Legend
                layout={isTablet ? "vertical" : "horizontal"}
                verticalAlign={isTablet ? "middle" : "bottom"}
                align={isTablet ? "right" : "center"}
                wrapperStyle={{
                  fontSize: 12,
                  paddingTop: 8,
                }}
              />}
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

const GraficoEstadoCobros = ({ facturas }: GraficoEstadoCobrosProps) => {
  const moneda = facturas[0]?.moneda || 'EUR';
  return <EstadoCobrosPie facturas={facturas} moneda={moneda} titulo="Estado de cobros" />;
};

export default GraficoEstadoCobros;
